import React, { useState, useEffect } from "react";
import FieldModelPanel from "@/components/FieldModelPanel";
import LeverageBoard from "@/components/LeverageBoard";
import { toast } from "sonner";
import { CaretDown, CaretUp, WarningCircle, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { humanStackLabel } from "@/lib/stackLabel";
import { TID } from "@/constants/testIds";
import PlayerPool from "@/components/PlayerPool";
import ComplianceBanner, { countSubstantiveTouches, MIN_TOUCHES } from "@/components/ComplianceBanner";
import GameCardGrid from "@/components/GameCardGrid";
import { sortPlayersDKOrder } from "@/lib/dkRoster";
import { PlayerHeadshot, TeamLogo, useHeadshotMap } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import CaptainCeilingChart from "@/components/CaptainCeilingChart";
import SlateModeBanner from "@/components/SlateModeBanner";
import StrategyModeBanner from "@/components/StrategyModeBanner";
import SundayAutoMode from "@/components/SundayAutoMode";
import DkTemplateFill from "@/components/DkTemplateFill";
import PortfolioAudit from "@/components/PortfolioAudit";
import EntryTemplate from "@/components/EntryTemplate";
import ShortfallBanner from "@/components/ShortfallBanner";
import SundayBanner from "@/components/SundayBanner";
import ActiveRuleset from "@/components/ActiveRuleset";
import BacktestPanel from "@/components/BacktestPanel";
import SpineHeatmap from "@/components/SpineHeatmap";
import {
  loadOverrides,
  saveOverrides,
  setActiveSlateKey,
  OVERRIDES_CHANGED_EVENT,
} from "@/lib/overridesStore";

const BOOK_DEFAULTS = {
  num_lineups: 150,
  salary_cap: 50000,
  salary_floor: 49500,
  primary_stack_size: 2,
  max_players_per_team: 4,
  sum_own_min: 75,
  sum_own_max: 125,
  min_low_owned: 1,
  low_owned_thresh: 5,
  global_variance: 0.08,
  max_exposure_pct: 25,
  // Fingerprint Variance™
  session_uniqueness_pct: 3.0,
  // Cluster Rotation™
  cluster_rotation_enabled: true,
  cluster_rotation_proj_band_pct: 5.0,
  cluster_rotation_salary_band: 400,
  cluster_rotation_swap_probability: 0.40,
  // Group rules
  team_caps: {},
  flex_bias: "any",
  // Sprint 58 — per-position FLEX exposure caps. Shape mirrors what the
  // backend expects: fractions of num_lineups. Blank / missing = uncapped.
  //   { RB: { min: 0.30, max: 0.60 }, WR: { max: 0.40 }, TE: { max: 0.15 } }
  flex_exposure_caps: {},
  // WarPaint stack distribution
  stack_pct_primary_only: 0.22,
  stack_pct_primary_secondary: 0.53,
  stack_pct_primary_secondary_tertiary: 0.25,
  // Betting-line biases
  qb_favorite_pct: 0.69,
  rb_home_favorite_pct: 0.62,
  wr_favorite_itt24_pct: 0.70,
  te_favorite_itt24_pct: 0.71,
  dst_ou_under47_pct: 0.84,
  dst_underdog_road_pct: 0.71,
  seed: 42,
};

export default function Optimizer({ onGenerated }) {
  const headshots = useHeadshotMap();
  const [cfg, setCfg] = useState(BOOK_DEFAULTS);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // Sprint 60.86 — Smart Optimizer sidebar roster expansion (Set of lineup_ids)
  // Sprint 60.92 — Default to ALL lineups expanded when a portfolio is
  // rendered. Users refused to click 150 dropdowns just to see rosters.
  const [expandedLineups, setExpandedLineups] = useState(() => new Set());

  // Auto-expand every lineup the moment a fresh result lands. Users can
  // still hit the Collapse All button to opt out.
  useEffect(() => {
    if (result?.lineups?.length) {
      setExpandedLineups(new Set(result.lineups.map((L) => L.lineup_id)));
    }
  }, [result?.lineups]);
  const [overrides, _setOverrides] = useState(() => loadOverrides());
  // Persist every override write to localStorage and broadcast so other
  // components (Pivot Board, Slate Intel widgets) share the same source
  // of truth. External writers dispatch `wp:overrides-changed`; we
  // subscribe below to re-hydrate this component's local copy.
  const setOverrides = (nextOrUpdater) => {
    _setOverrides((prev) => {
      const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
      saveOverrides(next);
      return next;
    });
  };
  const [presets, setPresets] = useState({});
  const [activePreset, setActivePreset] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("wp_active_preset")) || "milly_maker"
  );
  const [slateInfo, setSlateInfo] = useState(null);
  // Live contest target broadcast by the Smart Optimizer's contest picker
  // (MILLY / GPP / CASH / SHOWDOWN + field size). The strategy banners read
  // this first so they never lag behind the picker.
  const [contestTarget, setContestTarget] = useState(null);

  // Sprint 69 — the Active Ruleset panel follows the picker, not the last
  // build: preview the rules for the current selection until they match.
  const [rulePreview, setRulePreview] = useState(null);
  useEffect(() => {
    if (!contestTarget?.contestType) return undefined;
    const t = setTimeout(() => {
      api.rulesetPreview({
        contest_type: contestTarget.contestType, field_size: contestTarget.fieldSize || undefined,
        max_entries: contestTarget.maxEntries || undefined, num_lineups: cfg.num_lineups || undefined,
      }).then(setRulePreview).catch(() => setRulePreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [contestTarget?.contestType, contestTarget?.fieldSize, contestTarget?.maxEntries, cfg.num_lineups]);
  const builtMatchesPick = !!result?.config && !!contestTarget?.contestType
    && String(result.config.contest_type || "").toUpperCase() === String(contestTarget.contestType).toUpperCase()
    && (!contestTarget.fieldSize || Number(result.config.field_size || 0) === Number(contestTarget.fieldSize))
    && (!contestTarget.maxEntries || Number(result.config.max_entries_per_user || 0) === Number(contestTarget.maxEntries));

  useEffect(() => {
    const onContestTarget = (e) => {
      if (e?.detail?.contestType) setContestTarget(e.detail);
    };
    window.addEventListener("wp:contest-target-change", onContestTarget);
    return () => window.removeEventListener("wp:contest-target-change", onContestTarget);
  }, []);

  useEffect(() => {
    api.contestPresets()
      .then((d) => setPresets(d.presets || {}))
      .catch((e) => console.debug("[Optimizer] contestPresets fetch failed:", e?.message || e));
    const loadSlateInfo = () => api.slateInfo()
      .then((info) => { setActiveSlateKey(info?.active_slate_dg); setSlateInfo(info); })
      .catch((e) => console.debug("[Optimizer] slateInfo fetch failed:", e?.message || e));
    loadSlateInfo();
    window.addEventListener("wp:pool-refreshed", loadSlateInfo);
    // Sprint 60.43 — On mount, hydrate `result` from the last completed
    // generation so the ShortfallBanner survives tab switches. Without
    // this, a user who Generates on the Optimizer tab, visits Lineups,
    // and comes back sees an empty Optimizer even if their portfolio
    // is short — losing discovery of Fill-Gap. `lineups` is included
    // (may be [] if empty) so the Avg Projection / Avg Ownership tiles
    // on the right-rail don't crash on `result.lineups.length` access.
    api.getLineups().then((r) => {
      if (r && typeof r.count === "number" && typeof r.requested === "number" && r.requested > 0) {
        setResult({
          generated: r.count,
          requested: r.requested,
          generation_notes: r.generation_notes || [],
          lineups: r.lineups || [],
          config: r.config || {},
          showdown_rules: r.showdown_rules || r.config?.showdown_rules || null,
          stack_type_counts: r.config?.stack_type_counts || {},
          rotation_stats: null,
        });
      }
    }).catch((e) => console.debug("[Optimizer] getLineups hydrate failed:", e?.message || e));
    // If the user last selected the CUSTOM preset (from Slate Intel or the
    // Optimizer contest picker), pull their saved knobs into cfg on mount so
    // Smart Optimizer state reflects their tuning without a manual re-click. Fixes
    // iter_39 gap where switching tabs reset cfg to BOOK_DEFAULTS.
    if (typeof localStorage !== "undefined" &&
        localStorage.getItem("wp_active_preset") === "custom") {
      try {
        const raw = localStorage.getItem("wp_custom_preset_params");
        if (raw) {
          const params = JSON.parse(raw);
          if (params && typeof params === "object") {
            setCfg((prev) => ({ ...prev, ...params }));
          }
        }
      } catch (_) { /* noop */ }
    }
    // Re-hydrate overrides whenever another component (e.g. Pivot Board)
    // writes to the shared store. `_setOverrides` bypasses the persist
    // wrapper because the incoming value is already saved in localStorage.
    const onOverridesChanged = () => _setOverrides(loadOverrides());
    window.addEventListener(OVERRIDES_CHANGED_EVENT, onOverridesChanged);
    // Sprint 60.141 — Listen for fill-gap completion fired from ANY
    // ShortfallBanner (e.g. the My Lineups tab). Without this the
    // Smart Optimizer page's ShortfallBanner + right-rail keep
    // showing the pre-fill count (108/150) even though the backend
    // has 150. Refetch on the event so all lineup-count surfaces
    // stay in sync — no matter which page triggered the fill.
    const onFillGapCompleted = () => {
      api.getLineups().then((r) => {
        if (r && typeof r.count === "number") {
          setResult((prev) => prev
            ? {
                ...prev,
                generated: r.count,
                requested: r.requested || prev.requested,
                lineups: r.lineups || prev.lineups || [],
                generation_notes: r.generation_notes || prev.generation_notes || [],
              }
            : prev);
        }
      }).catch(() => { /* silent — banner will just stay one tick longer */ });
    };
    window.addEventListener("wp:fill-gap-completed", onFillGapCompleted);
    return () => {
      window.removeEventListener(OVERRIDES_CHANGED_EVENT, onOverridesChanged);
      window.removeEventListener("wp:fill-gap-completed", onFillGapCompleted);
      window.removeEventListener("wp:pool-refreshed", loadSlateInfo);
    };
  }, []);

  // React to preset broadcasts from other components (e.g. Kickoff Countdown tabs)
  useEffect(() => {
    const onPresetChange = (e) => {
      const nextKey = e?.detail?.preset;
      if (!nextKey || nextKey === activePreset) return;
      const source = e?.detail?.source || "external";
      const p = presets[nextKey];
      setActivePreset(nextKey);
      if (p && p.optimizer) {
        // Same rule as applyPreset: keep the user's num_lineups; only merge
        // strategy params. Prevents cross-tab preset picks (Kickoff Countdown,
        // Slate Intel) from silently resetting the count on preset swaps.
        const { num_lineups: _presetCount, ...strategy } = p.optimizer;
        setCfg((prev) => ({ ...prev, ...strategy }));
      }
      // Don't double-toast — kickoff-tab clicks already fire their own toast
      // from KickoffCountdown so the click feedback is universal (not gated
      // on Optimizer being mounted).
      if (source !== "kickoff-init" && source !== "kickoff-tab") {
        toast.success(`Contest target → ${p?.label || nextKey}`);
      }
    };
    window.addEventListener("wp:preset-change", onPresetChange);
    // Custom-params live-update: when the user tunes a knob in the Attack
    // Plan panel's Custom editor, merge those params into cfg immediately —
    // but only if the active preset actually IS custom (otherwise the built-in
    // preset's values should stay authoritative).
    const onCustomParamsChange = (e) => {
      const params = e?.detail?.params;
      if (!params) return;
      if (activePreset !== "custom") return;
      setCfg((prev) => ({ ...prev, ...params }));
    };
    window.addEventListener("wp:custom-preset-change", onCustomParamsChange);
    return () => {
      window.removeEventListener("wp:preset-change", onPresetChange);
      window.removeEventListener("wp:custom-preset-change", onCustomParamsChange);
    };
  }, [presets, activePreset]);

  const applyPreset = (key) => {
    setActivePreset(key);
    try { localStorage.setItem("wp_active_preset", key); }
    catch (_) { /* private-mode safe */ }
    try {
      window.dispatchEvent(new CustomEvent("wp:preset-change", { detail: { preset: key, source: "optimizer" } }));
    } catch (_) { /* CustomEvent unsupported */ }
    const p = presets[key];
    if (p && p.optimizer) {
      // Merge preset STRATEGY params (stack dist, ownership floor/ceiling,
      // variance, biases) but NEVER overwrite `num_lineups` — that's the
      // user's session choice, not a strategic parameter. Prior versions
      // silently reset the count to the preset's default (e.g. picking any
      // small-field preset stomped the user's 150 → 20), so users could
      // type 150, click a preset chip, and get 20 lineups with no warning.
      const { num_lineups: _presetCount, ...strategy } = p.optimizer;
      setCfg((prev) => ({ ...prev, ...strategy }));
      toast.success(`Contest target set → ${p.label}`);
    } else if (key === "custom") {
      // Pull the last-saved custom params so switching to Custom doesn't
      // leave the Optimizer running whatever the previous preset set.
      try {
        const raw = localStorage.getItem("wp_custom_preset_params");
        if (raw) {
          const params = JSON.parse(raw);
          if (params && typeof params === "object") {
            setCfg((prev) => ({ ...prev, ...params }));
          }
        }
      } catch (_) { /* noop */ }
      toast.success("Contest target → Custom (tune params in Slate Intel)");
    }
  };

  const stackSum = cfg.stack_pct_primary_only + cfg.stack_pct_primary_secondary + cfg.stack_pct_primary_secondary_tertiary;
  const touches = countSubstantiveTouches(overrides);
  const compliant = touches >= MIN_TOUCHES;

  const run = async () => {
    if (!compliant) {
      toast.warning(`Adjust ${MIN_TOUCHES - touches} more player${MIN_TOUCHES - touches === 1 ? "" : "s"} to unlock generation`);
      return;
    }
    setRunning(true);
    setResult(null);
    let pollId = null;
    try {
      // Async-job pattern (Cloudflare 100s timeout mitigation) — fire the
      // job (returns 202), poll progress, then fetch the final lineups.
      // Sprint 60.95 — read the slate-wide Game Lock board's excluded
      // games (persisted in localStorage under `wp_excluded_games`) and
      // wire them into the optimizer request so unlocked games' players
      // never enter the ILP pool.
      let excludedGames = [];
      try {
        const raw = localStorage.getItem("wp_excluded_games");
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) excludedGames = arr;
        }
      } catch { /* localStorage disabled — proceed unfiltered */ }
      await api.optimize({ ...cfg, player_overrides: overrides, excluded_games: excludedGames });
      const terminal = await new Promise((resolve, reject) => {
        pollId = setInterval(async () => {
          try {
            const p = await api.optimizeProgress();
            if (p?.phase === "done") { clearInterval(pollId); pollId = null; resolve(p); }
            else if (p?.phase === "error") {
              clearInterval(pollId); pollId = null;
              reject({ response: { data: { detail: p?.error?.detail || "Generation failed" } } });
            }
          } catch (_) { /* transient poll error — keep trying */ }
        }, 800);
      });
      const finalRes = await api.getLineups();
      const res = {
        generated: finalRes.count,
        requested: terminal.requested || finalRes.requested,
        generation_notes: terminal.generation_notes || finalRes.generation_notes || [],
        // Sprint 60.117 — regression fix: the post-optimize setResult
        // was dropping `lineups` (and `config`). That left the right-
        // rail Lineups panel and the Portfolio Shortfall banner both
        // reading `undefined` / short data, so a shortfall run (e.g.
        // 131/150) surfaced only a toast — no lineups, no cascade CTA.
        // Mirror the hydration payload shape so both panels render.
        lineups: finalRes.lineups || [],
        config: finalRes.config || {},
        showdown_rules: finalRes.showdown_rules || finalRes.config?.showdown_rules || null,
        stack_type_counts: finalRes.config?.stack_type_counts || {},
        rotation_stats: null,
      };
      setResult(res);
      const notes = res.generation_notes || [];
      const shortfall = res.generated < res.requested;
      if (res.generated === 0) {
        toast.warning(
          `Generated 0/${res.requested} — Your Player Pool constraints are infeasible.`,
          { description: notes[0] || "Loosen locks / excludes / min-exposure and try again.", duration: 12000 },
        );
      } else if (shortfall) {
        toast.warning(
          `Generated ${res.generated}/${res.requested} — did what I could within your Player Pool constraints.`,
          {
            description: `Scroll down to Portfolio Shortfall to auto-cascade the last ${res.requested - res.generated}.`,
            duration: 14000,
            action: {
              label: "Take me there",
              onClick: () => {
                const el = document.querySelector('[data-testid="optimizer-shortfall-banner"]') ||
                           document.querySelector('[data-testid$="-shortfall-banner"]');
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.classList.add("mm-pulse");
                  setTimeout(() => el.classList.remove("mm-pulse"), 2400);
                }
              },
            },
          },
        );
        notes.slice(0, 2).forEach((n, i) => setTimeout(() => toast.info(n, { duration: 10000 }), 400 * (i + 1)));
      } else {
        toast.success(`Generated ${res.generated}/${res.requested}`);
      }
      onGenerated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      const msg = typeof d === "string" ? d : d?.message || "Optimization failed";
      if (code === "POOL_STATE_STALE") {
        toast.error(msg, {
          duration: 12000,
          description: "Re-load your DK slate (Slate Selection tab) and click Generate again.",
        });
      } else if (code === "OPTIMIZE_ALREADY_RUNNING") {
        toast.warning(msg, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      if (pollId) clearInterval(pollId);
      setRunning(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <section className="lg:col-span-8 space-y-4">
        <SundayBanner />
        <SundayAutoMode
          slateType={slateInfo?.slate_type}
          onGenerated={(payload) => {
            // Sprint 60.43 — capture the terminal { count, requested }
            // payload so the Optimizer-page shortfall banner can render
            // in-context. Previously only the Lineups tab saw this data;
            // now users get the persistent banner + Fill-Gap CTA right
            // below the Generate button where the mistake was made.
            if (payload && typeof payload.count === "number" && typeof payload.requested === "number") {
              setResult((prev) => ({
                ...(prev || {}),
                generated: payload.count,
                requested: payload.requested,
                generation_notes: payload.generation_notes || prev?.generation_notes || [],
                // `finalRes` from SundayAutoMode is spread into payload,
                // so `payload.lineups` is the actual array from getLineups.
                // Fallback to prev to survive partial updates.
                lineups: payload.lineups || prev?.lineups || [],
                config: payload.config || prev?.config || null,
                showdown_rules: payload.showdown_rules || payload.config?.showdown_rules || prev?.showdown_rules || null,
                stack_type_counts: payload.config?.stack_type_counts || prev?.stack_type_counts || {},
              }));
            }
            onGenerated?.(payload);
          }}
          userOverrides={overrides}
          onOverridesChange={setOverrides}
        />
        <details className="group" data-testid="build-field-model-fold">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3 bg-[#121212] border border-zinc-800 hover:border-[#FFCC00]/50 px-4 py-2.5 mono text-[10px] uppercase tracking-widest text-zinc-300">
            <span><span className="text-[#FFCC00] font-bold">Field Model</span> · bring your own projections (optional) — sharpen the shadow field with exports from the sites you subscribe to</span>
            <span className="text-zinc-500 group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="mt-2"><FieldModelPanel /></div>
        </details>
        <details className="group" data-testid="build-leverage-fold">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3 bg-[#121212] border border-zinc-800 hover:border-[#39FF14]/50 px-4 py-2.5 mono text-[10px] uppercase tracking-widest text-zinc-300">
            <span><span className="text-[#39FF14] font-bold">Leverage Score</span> · our sim Optimal% vs the field's recounted ownership — players and stacks</span>
            <span className="text-zinc-500 group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="mt-2"><LeverageBoard /></div>
        </details>
        {/* Sprint 60.43 — Shortfall banner ALSO surfaces on the Optimizer
            page (not just Lineups). Users who see "Generated 106/150" as
            a transient toast often miss it and keep hitting Generate;
            rendering the persistent banner right below the Generate CTA
            makes Fill-Gap discoverable in-context and explains the
            cascade so users trust the "we relax one rule at a time"
            copy instead of guessing what happens. */}
        {result && result.requested > 0 && result.generated < result.requested && (
          <ShortfallBanner
            requested={result.requested}
            count={result.generated}
            notes={result.generation_notes || []}
            baseConfig={cfg}
            onFilled={(fillResp) => {
              // Sprint 60.117 — use `fillResp.total` (authoritative from the
              // write pod) as the source of truth for the new generated
              // count so the banner + shortfall alert hide immediately.
              // Then fire /lineups to refresh `result.lineups` (the write
              // pod either still has the fresh state or Mongo hydration
              // pulls it — either way we already know the count is correct
              // even if the lineup array lags one tick).
              const newTotal = fillResp?.total ?? null;
              if (newTotal !== null) {
                // Immediate optimistic update so the banner disappears in
                // the same tick as the success toast — no waiting on GET.
                setResult((prev) => prev
                  ? { ...prev, generated: newTotal }
                  : prev);
              }
              api.getLineups().then((r) => {
                setResult((prev) => prev
                  ? {
                      ...prev,
                      // Prefer fill-gap total (authoritative) over the GET
                      // count in case the GET hits a stale-cache pod. Only
                      // downgrade when the GET returns MORE (extremely rare).
                      generated: Math.max(newTotal || 0, r.count || 0),
                      requested: r.requested || prev.requested,
                      lineups: (r.lineups && r.lineups.length >= (newTotal || 0))
                        ? r.lineups
                        : (prev.lineups || []),
                      config: r.config || prev.config,
                      generation_notes: r.generation_notes || prev.generation_notes || [],
                    }
                  : prev);
                onGenerated?.();
              }).catch(() => {
                // GET failed — still count fill as success via optimistic update.
                onGenerated?.();
              });
            }}
            testIdPrefix="optimizer-shortfall"
          />
        )}
        <SlateModeBanner
          slateType={slateInfo?.slate_type}
          slateMeta={slateInfo}
          contestType={contestTarget?.contestType}
          presetKey={activePreset}
        />
        <StrategyModeBanner
          presets={presets}
          activeKey={activePreset}
          contestType={contestTarget?.contestType}
          fieldSize={contestTarget?.fieldSize}
        />
        <ComplianceBanner overrides={overrides} />
        {/* Sprint 60.95 — Slate-wide Game Lock board. Locked games'
            players go into the ILP pool; unlocked games' players are
            filtered out via `excluded_games` in the optimize request.
            Same UI as Slate Intel so lock state syncs across tabs. */}
        <GameCardGrid />
        <PlayerPool overrides={overrides} onChange={setOverrides} showdown={slateInfo?.slate_type === "SHOWDOWN"} />
      </section>

      <aside className="lg:col-span-4 space-y-4">
        {(result && (builtMatchesPick || !rulePreview))
          ? <ActiveRuleset config={result.config} showdownRules={result.showdown_rules} lineups={result.lineups} />
          : rulePreview && <ActiveRuleset preview config={rulePreview.config} showdownRules={rulePreview.showdown_rules} lineups={[]} />}
        <BacktestPanel />
        {result && (
          <div className="bg-[#0a0a0a] border border-zinc-800 p-6 mm-ai-glow">
            <div className="overline mb-4">Portfolio Summary</div>
            <div className="space-y-3">
              <MetricRow label="Generated" value={`${result.generated}/${result.requested}`} />
              <div className="pt-2 border-t border-zinc-900">
                <div className="overline mb-2">Stack Distribution</div>
                {Object.entries(result.stack_type_counts || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs mono py-0.5">
                    <span className="text-zinc-300">{k.replace(/_/g, " ")}</span>
                    <span className="text-[#39FF14]">{v} ({((v / result.generated) * 100).toFixed(0)}%)</span>
                  </div>
                ))}
              </div>
              <MetricRow label="Avg Projection" value={result.lineups.length ? (result.lineups.reduce((s, l) => s + l.projection, 0) / result.lineups.length).toFixed(1) : "—"} />
              <MetricRow label="Avg Total Ownership" value={result.lineups.length ? (result.lineups.reduce((s, l) => s + l.total_own, 0) / result.lineups.length).toFixed(1) + "%" : "—"} />
            </div>
          </div>
        )}

        {result && result.lineups && result.lineups.length > 0 && (
          <SpineHeatmap
            lineups={result.lineups}
            showdownRules={result.showdown_rules}
            config={result.config}
          />
        )}
        {result && result.lineups && result.lineups.length > 0 && (
          <div className="bg-[#0a0a0a] border border-zinc-800" data-testid="optimizer-lineups-panel">
            {/* Sprint 60.86 — Inline shortfall alert with jump-to-fix CTA */}
            {result.generated < result.requested && (
              <div className="border-b border-[#FFCC00]/40 bg-[#FFCC00]/5 px-4 py-2.5 flex items-start gap-3">
                <WarningCircle size={16} weight="fill" className="text-[#FFCC00] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">
                    Shortfall · {result.generated}/{result.requested} lineups built
                  </div>
                  <div className="text-[11px] text-zinc-300 leading-relaxed mt-0.5">
                    ILP hit a constraint wall. Scroll down to <span className="text-[#39FF14] font-bold">Portfolio Shortfall</span> — one click cascades relaxed rules to finish the last <span className="text-[#39FF14] font-bold">{result.requested - result.generated}</span> without touching the {result.generated} you already have.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Sprint 60.116 — fix: ShortfallBanner renders as
                    // `${testIdPrefix}-banner` = "optimizer-shortfall-banner".
                    // Prior queries used stale prefix guesses and matched
                    // nothing, so the button silently no-op'd. Match the
                    // actual rendered testid (with a legacy fallback).
                    const el = document.querySelector('[data-testid="optimizer-shortfall-banner"]') ||
                               document.querySelector('[data-testid$="-shortfall-banner"]');
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "center" });
                      el.classList.add("mm-pulse");
                      setTimeout(() => el.classList.remove("mm-pulse"), 2400);
                    }
                  }}
                  data-testid="optimizer-shortfall-jump"
                  className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black font-bold whitespace-nowrap"
                >
                  Take me there ↓
                </button>
              </div>
            )}
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div className="overline">Generated Lineups · {result.lineups.length}</div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setExpandedLineups(new Set(result.lineups.map((L) => L.lineup_id)))}
                  data-testid="opt-lineups-expand-all"
                  className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14]"
                >Expand All</button>
                <button
                  type="button"
                  onClick={() => setExpandedLineups(new Set())}
                  data-testid="opt-lineups-collapse-all"
                  className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-white"
                >Collapse</button>
                <button
                  type="button"
                  data-testid="opt-lineups-clear"
                  onClick={async () => {
                    if (!window.confirm(
                      `Clear all ${result.lineups.length} generated lineups?\n\n` +
                      "This wipes the Smart Optimizer's saved portfolio so it won't\n" +
                      "leak into the Simulation tab. Manual Optimizer lineups are\n" +
                      "kept in a separate bucket and won't be affected."
                    )) return;
                    try {
                      const r = await api.clearLineups();
                      setResult(null);
                      setExpandedLineups(new Set());
                      window.dispatchEvent(new CustomEvent("wp:lineups-cleared"));
                      toast.success(`Cleared ${r.cleared} lineups from Smart Optimizer.`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Clear failed");
                    }
                  }}
                  title={`Clear all ${result.lineups.length} generated lineups from the Smart Optimizer portfolio. Manual Optimizer lineups are kept separately.`}
                  className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest px-2 py-1 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black font-bold transition-colors"
                >
                  <Trash size={11} weight="bold" />
                  Clear Lineups
                </button>
                <button
                  type="button"
                  data-testid="open-lineups-tab"
                  onClick={() => {
                    try { window.dispatchEvent(new CustomEvent("wp:goto-tab", { detail: { tab: "lineups" } })); }
                    catch { /* noop */ }
                  }}
                  className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] transition-colors"
                >
                  View all →
                </button>
              </div>
            </div>
            <div className="max-h-[560px] overflow-y-auto wp-scroll" data-testid="opt-lineups-scroll">
              <table className="w-full text-xs mono">
                <thead className="bg-black sticky top-0 z-10">
                  <tr className="text-left text-zinc-500 uppercase tracking-widest text-[9px]">
                    <th className="px-2 py-2 w-4"></th>
                    <th className="px-3 py-2">#</th>
                    <th className="px-2 py-2">Stack</th>
                    <th className="px-2 py-2 text-right">Salary</th>
                    <th className="px-2 py-2 text-right">Proj</th>
                    <th className="px-3 py-2 text-right">Own%</th>
                  </tr>
                </thead>
                <tbody>
                  {[...result.lineups].sort((a, b) => (b?.projection || 0) - (a?.projection || 0)).map((L, rank) => {
                    const expanded = expandedLineups.has(L.lineup_id);
                    const toggle = () => setExpandedLineups((prev) => {
                      const next = new Set(prev);
                      if (next.has(L.lineup_id)) next.delete(L.lineup_id); else next.add(L.lineup_id);
                      return next;
                    });
                    return (
                      <React.Fragment key={L.lineup_id}>
                        <tr
                          onClick={toggle}
                          className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                          data-testid={`opt-lineup-row-${L.lineup_id}`}
                        >
                          <td className="px-2 py-1.5 text-zinc-600">
                            {expanded ? <CaretDown size={11} /> : <CaretUp size={11} weight="fill" style={{ transform: "rotate(90deg)" }} />}
                          </td>
                          <td className="px-3 py-1.5 font-bold text-zinc-300" title={`Ranked by projection · build id ${L.lineup_id}`} data-testid={`opt-lineup-rank-${L.lineup_id}`}>{rank + 1}</td>
                          <td className="px-2 py-1.5 text-zinc-400 truncate max-w-[110px]" title={`${L.stack_desc || L.stack_type} · shape: ${L.stack_type || "?"}`}>
                            {humanStackLabel(L)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-zinc-400">${(L.salary || 0).toLocaleString()}</td>
                          <td className="px-2 py-1.5 text-right text-[#39FF14] font-bold">{Number(L.projection || 0).toFixed(1)}</td>
                          <td className={`px-3 py-1.5 text-right ${L.total_own > 100 ? "text-[#FF3B30]" : L.total_own > 90 ? "text-[#FFCC00]" : "text-zinc-300"}`}>
                            {Number(L.total_own || 0).toFixed(1)}%
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="bg-black/40">
                            <td colSpan={6} className="px-3 py-2">
                              <div className="grid grid-cols-3 gap-1" data-testid={`opt-lineup-roster-${L.lineup_id}`}>
                                {sortPlayersDKOrder(L.players || []).map((p, j) => {
                                  const isCpt = String(p.roster_slot || "").toUpperCase() === "CPT";
                                  const sal = isCpt ? (p.cpt_salary || Math.round((p.salary || 0) * 1.5)) : (p.salary || 0);
                                  const proj = Number(p.projection || p.our_proj || 0) * (isCpt ? 1.5 : 1);
                                  return (
                                    <div key={j} data-testid={`opt-lineup-player-${L.lineup_id}-${p.dk_id}`}
                                      className={`border bg-[#0a0a0a] px-2 py-1 relative overflow-hidden ${isCpt ? "border-[#FFCC00]/70" : "border-zinc-800"}`}>
                                      <div className="absolute -right-2 -top-2 opacity-[0.07] pointer-events-none"><TeamLogo team={p.team} size={48} withGlow={false} /></div>
                                      <div className="flex items-center gap-1.5">
                                        <span className={`mono text-[8px] uppercase tracking-widest ${isCpt ? "text-[#FFCC00] font-black" : "text-zinc-500"}`}>
                                          {p.roster_slot || p.position}
                                        </span>
                                        {isCpt && <span data-testid={`opt-cpt-badge-${L.lineup_id}`} className="mono text-[7px] px-1 border border-[#FFCC00]/70 text-[#FFCC00] uppercase">1.5×</span>}
                                      </div>
                                      <div className="flex items-center gap-1.5 mt-0.5">
                                        <PlayerHeadshot player={p} sleeperId={headshots[String(p.dk_id)]} size={22} />
                                        <div className="min-w-0">
                                          <div className="mono text-[10px] text-white font-bold truncate">{p.name}</div>
                                          <div className="mono text-[8px] text-zinc-400 flex items-center gap-1">
                                            {p.position} · <TeamLogo team={p.team} size={11} withGlow={false} /><span className="font-black" style={{ color: teamTextColor(p.team) }}>{p.team}</span> · ${sal.toLocaleString()} · {proj.toFixed(1)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {result?.lineups?.length > 0 && result.lineups[0]?.stack_type === "SHOWDOWN" && (
          <CaptainCeilingChart
            lineups={result.lineups}
            capPct={result.config?.cpt_max_share_pct ?? result.showdown_rules?.cpt_max_exposure_pct}
            capSource={result.showdown_rules?.cpt_cap_source}
            overrides={overrides}
            onOverridesChange={setOverrides}
            running={running}
          />
        )}

      </aside>
      {/* Sprint 60.77 — DK Entry Template fill (Smart source) */}
      <div className="lg:col-span-12">
        <DkTemplateFill source="smart" />
        <div className="mt-3"><PortfolioAudit /></div>
        {/* Contest Ops where people actually build — same component the War
            Room uses, so one entry template serves both. */}
        <details className="group" data-testid="build-contest-ops">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3 bg-[#121212] border border-zinc-800 hover:border-[#FFCC00]/50 px-4 py-2.5 mono text-[10px] uppercase tracking-widest text-zinc-300">
            <span><span className="text-[#FFCC00] font-bold">Contest Ops</span> · multiple tournaments from one DK entries file</span>
            <span className="text-zinc-500 group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="mt-2"><EntryTemplate /></div>
        </details>
      </div>
    </div>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="overline">{label}</span>
      <span className="mono font-bold text-[#39FF14]">{value}</span>
    </div>
  );
}
